/**
 * RequestBodyEditor Component
 *
 * Displays a JSON editor for request bodies in API endpoints.
 * Handles request body input for POST/PUT/PATCH methods with JSON validation.
 *
 * Requirements:
 * - 14.1: Display JSON editor when `requiresBody: true`
 * - 14.2: Hide editor for GET/DELETE methods or when `requiresBody: false`
 * - 14.3: Validate JSON syntax before allowing send
 * - 14.4: Display request body schema as reference if available
 * - 14.5: Handle body value changes
 */

import { useState, useEffect } from 'react';
import { ParsedEndpoint } from '../types';

export interface RequestBodyEditorProps {
  /** The selected endpoint */
  endpoint: ParsedEndpoint | null;
  /** Current request body value (as object or string) */
  value: any;
  /** Callback when body value changes */
  onChange: (value: any) => void;
  /** Callback to report validation status */
  onValidationChange?: (isValid: boolean, error?: string) => void;
}

/**
 * RequestBodyEditor component renders a JSON editor for request bodies.
 *
 * @param endpoint - The selected endpoint (determines if body is needed)
 * @param value - Current request body value
 * @param onChange - Callback function when body changes
 * @param onValidationChange - Callback to report validation status
 */
export const RequestBodyEditor = ({
  endpoint,
  value,
  onChange,
  onValidationChange,
}: RequestBodyEditorProps) => {
  const [jsonText, setJsonText] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');
  const [showSchema, setShowSchema] = useState<boolean>(false);

  // Initialize JSON text from value
  useEffect(() => {
    if (value) {
      try {
        setJsonText(JSON.stringify(value, null, 2));
        setValidationError('');
        onValidationChange?.(true);
      } catch (error) {
        setJsonText(typeof value === 'string' ? value : '');
      }
    } else {
      setJsonText('');
      setValidationError('');
      onValidationChange?.(true);
    }
  }, [value, onValidationChange]);

  // Don't render if no endpoint selected
  if (!endpoint) {
    return null;
  }

  // Don't render for GET/DELETE methods or when body is not required
  const shouldHideEditor =
    endpoint.method === 'GET' || endpoint.method === 'DELETE' || !endpoint.requiresBody;

  if (shouldHideEditor) {
    return null;
  }

  /**
   * Handle JSON text change with validation
   */
  const handleTextChange = (text: string) => {
    setJsonText(text);

    // If empty, clear validation error and notify parent
    if (text.trim() === '') {
      setValidationError('');
      onChange(null);
      onValidationChange?.(true);
      return;
    }

    // Validate JSON syntax
    try {
      const parsed = JSON.parse(text);
      setValidationError('');
      onChange(parsed);
      onValidationChange?.(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON syntax';
      setValidationError(errorMessage);
      onValidationChange?.(false, errorMessage);
    }
  };

  /**
   * Format JSON with proper indentation
   */
  const handleFormat = () => {
    try {
      const parsed = JSON.parse(jsonText);
      const formatted = JSON.stringify(parsed, null, 2);
      setJsonText(formatted);
      setValidationError('');
      onChange(parsed);
      onValidationChange?.(true);
    } catch (error) {
      // If JSON is invalid, show error but don't change text
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON syntax';
      setValidationError(errorMessage);
      onValidationChange?.(false, errorMessage);
    }
  };

  /**
   * Clear the editor
   */
  const handleClear = () => {
    setJsonText('');
    setValidationError('');
    onChange(null);
    onValidationChange?.(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Request Body</h3>
        <div className="flex items-center space-x-2">
          {endpoint.requestBodySchema && (
            <button
              type="button"
              onClick={() => setShowSchema(!showSchema)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {showSchema ? 'Hide Schema' : 'Show Schema'}
            </button>
          )}
          <button
            type="button"
            onClick={handleFormat}
            className="px-3 py-1 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
          >
            Format
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-1 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Schema Display */}
      {showSchema && endpoint.requestBodySchema && (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body Schema</h4>
          <pre className="text-xs text-gray-600 overflow-x-auto">
            {JSON.stringify(endpoint.requestBodySchema, null, 2)}
          </pre>
        </div>
      )}

      {/* JSON Editor */}
      <div className="space-y-2">
        <textarea
          id="request-body-editor"
          value={jsonText}
          onChange={(e) => handleTextChange(e.target.value)}
          className={`w-full h-64 px-3 py-2 font-mono text-sm border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            validationError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500'
          }`}
          placeholder='Enter JSON request body, e.g., {"key": "value"}'
          spellCheck={false}
        />

        {/* Validation Error */}
        {validationError && (
          <div className="flex items-start space-x-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="font-medium">Invalid JSON</p>
              <p className="mt-1">{validationError}</p>
            </div>
          </div>
        )}

        {/* Success Indicator */}
        {!validationError && jsonText.trim() !== '' && (
          <div className="flex items-center space-x-2 text-sm text-green-600">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Valid JSON</span>
          </div>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-sm text-gray-500">
        Enter a valid JSON object for the request body. Use the Format button to auto-format your
        JSON.
      </p>
    </div>
  );
};

export default RequestBodyEditor;
